import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, ValidationErrors } from '@angular/forms';
import { DateHelper } from "../../../../core/helpers/date-helper";
import { PatientDetail } from "../../../../core/models/patient-detail.model";
import { PatientService } from "../../../../core/services/patient.service";
import { LogHelper } from "../../../../core/helpers/log.helper";
import { AlertService } from "../../../../shared/alert/alert.service";
import { ObjectHelper } from '../../../../core/helpers/object.helper';
import {
  UpdatePatientAdditionalDetails
} from "../../../../core/services/interfaces/update-patient-additional-details.interface";
import { AuthService } from "../../../../core/services/auth.service";
import { RoleNames } from "../../../../core/constants/role-names";
import { Permissions } from "../../../../core/constants/permissions";

@Component({
  selector: 'app-patient-detail-additional',
  templateUrl: './patient-detail-additional.component.html',
  styleUrls: ['./patient-detail-additional.component.scss']
})
export class PatientDetailAdditionalComponent implements OnInit {
  @ViewChild('passportExpiry') passportExpiry: ElementRef;
  @ViewChild('dateOfBirth') dateOfBirth: ElementRef;
  @Output() patientInfoUpdated = new EventEmitter();
  @Input() patient: PatientDetail;
  form: UntypedFormGroup;
  isFormProcessing = false;
  phoneRawText = '';

  constructor(private readonly patientService: PatientService, private readonly alertService: AlertService, protected readonly authService: AuthService) { }

  ngOnInit() {
    this.form = new UntypedFormGroup({
      passportName: new UntypedFormControl(this.patient.passportName),
      passportExpiry: new UntypedFormControl(ObjectHelper.isUndefinedNullOrEmpty(this.patient.passportExpiry) ? null : DateHelper.dateToYyyyMmDd(this.patient.passportExpiry)),
      dateOfBirth: new UntypedFormControl(
        ObjectHelper.isUndefinedNullOrEmpty(this.patient.dateOfBirth) ? null : DateHelper.dateToYyyyMmDd(this.patient.dateOfBirth),
        { validators: this.notFutureDateValidator, updateOn: 'blur' }
      ),
      phone: new UntypedFormControl(this.patient.phone),
      nextOfKin: new UntypedFormControl(this.patient.nextOfKin),
      nextOfKinPhone: new UntypedFormControl(this.patient.nextOfKinPhone),
      passportNumber: new UntypedFormControl(this.patient.passportNumber),
      visaInfo: new UntypedFormControl(this.patient.visaInfo),
      otherNotes: new UntypedFormControl(this.patient.otherNotes),
      travellingWithCaregiver: new UntypedFormControl(this.patient.travellingWithCaregiver),
      caregiverDetails: new UntypedFormControl(this.patient.caregiverDetails)
    }, { validators: this.passportExpiryValidator });

    this.phoneRawText = this.patient.phone || '';

    if (!this.authService.hasPermission(Permissions.PatientDetailsWrite)) {
      this.form.get('passportName').disable();
      this.form.get('passportExpiry').disable();
      this.form.get('dateOfBirth').disable();
      this.form.get('phone').disable();
      this.form.get('nextOfKin').disable();
      this.form.get('nextOfKinPhone').disable();
      this.form.get('visaInfo').disable();
      this.form.get('otherNotes').disable();
      this.form.get('travellingWithCaregiver').disable();
      this.form.get('caregiverDetails').disable();
      this.form.get('passportNumber').disable();
    }
  }

  onFormSubmit() {
    if (this.form.valid && !this.phoneValueMismatch) {
      this.isFormProcessing = true;

      // Custom Data
      const customData: any = {};
      this.patient.customDataFields.forEach(field => {
        const control = this.form.get(field.id);
        if (control) {
          customData[field.id] = control.value;
        }
      });

      const dto: UpdatePatientAdditionalDetails = {
        passportName: this.form.get('passportName').value,
        passportExpiryDate: this.form.get('passportExpiry').value,
        dob: this.form.get('dateOfBirth').value,
        phone: this.form.get('phone').value,
        nextOfKin: this.form.get('nextOfKin').value,
        nextOfKinPhone: this.form.get('nextOfKinPhone').value,
        passportNumber: this.form.get('passportNumber').value,
        visaInfo: this.form.get('visaInfo').value,
        otherNotes: this.form.get('otherNotes').value,
        travellingWithCaregiver: this.form.get('travellingWithCaregiver').value,
        caregiverDetails: this.form.get('caregiverDetails').value,
        customData: customData
      };

      this.patientService.updatePatientAdditionalDetails(this.patient.id, dto).subscribe({
        next: () => {
          this.patientInfoUpdated.emit();

          this.alertService.showSuccessAlert('Patient Updated Successfully');
          this.isFormProcessing = false;
          this.form.markAsUntouched();
        },
        error: error => {
          LogHelper.log(error);
          this.isFormProcessing = false;
          this.alertService.showWarningAlert('Unable to update patient details!');
        }
      });
    }
  }

  private notFutureDateValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;

    if (!value) {
      return null;
    }

    // showMaskTyped leaves the placeholder character in the value for any digits not yet typed
    if (value.length !== 10 || value.includes('*'))
    {
      return { incompleteDate: true };
    }

    // Parse yyyy-mm-dd format
    const inputDate = new Date(value);

    if (isNaN(inputDate.getTime()))
    {
      return { incompleteDate: true };
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to start of day for comparison

    if (inputDate > today)
    {
      return { futureDate: true };
    }

    return null;
  }

  private passportExpiryValidator(control: AbstractControl): ValidationErrors | null {
    const passportExpiry = control.get('passportExpiry')?.value;
    const dateOfBirth = control.get('dateOfBirth')?.value;

    if (!passportExpiry || !dateOfBirth) {
      return null;
    }

    // Only validate if both dates are complete (yyyy/mm/dd = 10 characters)
    if (passportExpiry.length !== 10 || dateOfBirth.length !== 10) {
      return null;
    }

    // Parse yyyy-mm-dd format
    const expiryDate = new Date(passportExpiry);
    const birthDate = new Date(dateOfBirth);

    if (expiryDate < birthDate) {
      return { passportExpiryBeforeBirth: true };
    }

    return null;
  }

  get dateOfBirthHasFutureError(): boolean {
    const control = this.form.get('dateOfBirth');
    return (control?.touched && control?.hasError('futureDate')) ?? false;
  }

  get dateOfBirthIsIncomplete(): boolean {
    const control = this.form.get('dateOfBirth');
    return (control?.touched && control?.hasError('incompleteDate')) ?? false;
  }

  onPhoneRawInput(event: Event): void {
    this.phoneRawText = (event.target as HTMLInputElement).value ?? '';

    // ngx-mat-input-tel silently no-ops instead of propagating the clear when the field is emptied, leaving the previous value committed
    if (!this.phoneRawText.trim() && this.form.get('phone').value)
    {
      this.form.get('phone').setValue(null);
      this.form.get('phone').markAsDirty();
    }
  }

  get phoneValueMismatch(): boolean {
    const hasVisibleText = this.phoneRawText.trim().length > 0;
    const committedValue = this.form.get('phone').value;
    const hasCommittedValue = committedValue != null && committedValue !== '';

    return hasVisibleText && !hasCommittedValue;
  }

  protected readonly RoleNames = RoleNames;
  protected readonly Permissions = Permissions;
}
